import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import AdminLayout from "@/components/AdminLayout";
import FilterBar from "@/components/FilterBar";
import { CheckCircle2, AlertCircle, Lock, Unlock, Loader2, Play, Eye, Banknote, RotateCcw } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const STEPS = [
  { id: "validate", label: "1. Validate", icon: CheckCircle2 },
  { id: "compute",  label: "2. Compute",  icon: Play },
  { id: "review",   label: "3. Review",   icon: Eye },
  { id: "lock",     label: "4. Lock",     icon: Lock },
  { id: "disburse", label: "5. Disburse", icon: Banknote },
];

const MONTHS = ["January","February","March","April","May","June","July","August","September","October","November","December"];

export default function PayrollWizard() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const today = new Date();
  const [month, setMonth] = useState(today.getMonth() + 1);
  const [year, setYear] = useState(today.getFullYear());
  const [run, setRun] = useState(null);
  const [active, setActive] = useState("validate");
  const [validation, setValidation] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [payslips, setPayslips] = useState([]);
  // Phase 17 — reverse-run state
  const [reversal, setReversal] = useState({ pending: null, history: [] });
  const [reverseOpen, setReverseOpen] = useState(false);
  const [reverseReason, setReverseReason] = useState("");
  const [reverseErr, setReverseErr] = useState("");
  const [decideOpen, setDecideOpen] = useState(null); // "approve" | "reject" | null
  const [decideNote, setDecideNote] = useState("");
  const [decideErr, setDecideErr] = useState("");
  // Soft-unlock (admin + reason) — keeps payslips; distinct from Reverse
  const [unlockOpen, setUnlockOpen] = useState(false);
  const [unlockReason, setUnlockReason] = useState("");
  const [unlockErr, setUnlockErr] = useState("");

  const loadReversal = async (runId) => {
    if (!runId) return;
    try {
      const r = await api.get(`/payroll-runs/${runId}/reversal`);
      setReversal(r.data || { pending: null, history: [] });
    } catch { /* ignore */ }
  };

  const downloadFile = async (path, name) => {
    const res = await api.get(path, { responseType: "blob" });
    const blob = new Blob([res.data]);
    const link = document.createElement("a");
    link.href = window.URL.createObjectURL(blob);
    link.download = name;
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  const startRun = async () => {
    setBusy(true);
    setMsg("");
    try {
      const r = await api.post("/payroll-runs", { month, year });
      setRun(r.data);
      // Locked → Lock step; after unlock → Compute (mandatory); else resume
      if (r.data?.status === "locked") setActive("lock");
      else if (r.data?.requires_compute) setActive("compute");
      else setActive(r.data.steps_completed?.length ? "review" : "validate");
      await loadReversal(r.data.id);
    } catch (e) {
      setMsg(e?.response?.data?.detail || "Failed to start run");
    } finally {
      setBusy(false);
    }
  };

  const runLock = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/payroll-runs/${run.id}/lock`);
      setRun(r.data);
      setActive("disburse");
      setMsg("Payroll run locked. Totals: " + JSON.stringify(r.data.totals));
      await loadReversal(r.data.id);
    } catch (e) {
      setMsg(e?.response?.data?.detail || "Lock failed");
    } finally { setBusy(false); }
  };

  const submitUnlock = async () => {
    if (!unlockReason.trim()) { setUnlockErr("Reason is mandatory"); return; }
    setBusy(true); setUnlockErr("");
    try {
      const r = await api.post(`/payroll-runs/${run.id}/unlock`, { reason: unlockReason.trim() });
      setRun(r.data);
      setUnlockOpen(false);
      setUnlockReason("");
      // Unlock only opens the period — must Compute again to re-add / remove by current flags
      setActive("compute");
      setMsg(
        "Month unlocked. Click Compute Payroll so Include in payment changes are applied (add/remove employees). Unlock alone does not regenerate the register."
      );
    } catch (e) {
      setUnlockErr(e?.response?.data?.detail || "Unlock failed");
    } finally { setBusy(false); }
  };

  const markAllPaid = async () => {
    if (!confirm(`Mark all payslips for ${MONTHS[month - 1]} ${year} as Paid?`)) return;
    setBusy(true);
    try {
      const r = await api.post("/payslips/mark-paid", null, { params: { month, year } });
      setMsg(`Marked ${r.data.updated ?? r.data.matched ?? 0} payslip(s) as Paid.`);
      const pr = await api.get("/payslips", { params: { month, year } });
      setPayslips(pr.data || []);
    } catch (e) {
      setMsg(e?.response?.data?.detail || "Failed to mark payslips Paid");
    } finally { setBusy(false); }
  };

  // Phase 17 — Reverse Run handlers
  const submitReverseRequest = async () => {
    if (!reverseReason.trim()) { setReverseErr("Reason is mandatory"); return; }
    setBusy(true); setReverseErr("");
    try {
      await api.post(`/payroll-runs/${run.id}/reverse-request`, { reason: reverseReason.trim() });
      setReverseOpen(false);
      setReverseReason("");
      setMsg("Reversal request submitted — awaiting approval from another admin.");
      await loadReversal(run.id);
    } catch (e) {
      setReverseErr(e?.response?.data?.detail || "Failed to submit request");
    } finally { setBusy(false); }
  };

  const submitDecision = async () => {
    if (!decideOpen) return;
    setBusy(true); setDecideErr("");
    try {
      const url = `/payroll-runs/${run.id}/reverse-${decideOpen}`;
      await api.post(url, { note: decideNote });
      setDecideOpen(null); setDecideNote("");
      if (decideOpen === "approve") {
        // Refresh run state — it's now draft, payslips voided
        const r2 = await api.post("/payroll-runs", { month, year });
        setRun(r2.data);
        setActive("compute");
        setMsg("Reversal approved. Run reset to DRAFT — re-compute payslips for this period.");
        setPayslips([]);
      } else {
        setMsg("Reversal rejected. Run remains LOCKED.");
      }
      await loadReversal(run.id);
    } catch (e) {
      setDecideErr(e?.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  const runValidate = async () => {
    if (!run) return;
    setBusy(true);
    try {
      const r = await api.get(`/payroll-runs/${run.id}/validate`);
      setValidation(r.data);
      setActive("compute");
    } finally { setBusy(false); }
  };

  const runCompute = async () => {
    setBusy(true);
    setMsg("Reconciling payroll rows…");
    try {
      const r = await api.post("/payslips/generate-bulk", null, { params: { month, year } });
      const payOnly = r.data.payment_only_generated || 0;
      const removed = r.data.removed || 0;
      let msg = `Computed ${r.data.generated || 0} new row(s), recomputed ${r.data.recomputed || 0} existing. Skipped ${r.data.skipped || 0}`;
      if (r.data.skipped_existing) msg += ` (${r.data.skipped_existing} already exist)`;
      msg += ".";
      if (payOnly > 0) msg += ` ${payOnly} payment-only (register/bank only — no slip PDF).`;
      if (removed > 0) msg += ` Removed ${removed} (Include in payment=No).`;
      const newly = r.data.newly_included || [];
      if (newly.length) {
        msg += ` Newly included: ${newly
          .slice(0, 8)
          .map((x) => x.code)
          .join(", ")}${newly.length > 8 ? "…" : ""}.`;
      }
      setMsg(msg);
      // Refresh run so requires_compute clears
      if (run?.id) {
        const rr = await api.post("/payroll-runs", { month, year });
        setRun(rr.data);
      }
      setActive("review");
      const pr = await api.get("/payslips", { params: { month, year } });
      setPayslips(pr.data || []);
    } catch (e) {
      setMsg(e?.response?.data?.detail || "Compute failed");
    } finally { setBusy(false); }
  };

  const needsCompute = Boolean(run?.requires_compute);
  const goToStep = (stepId) => {
    if (needsCompute && ["review", "lock", "disburse"].includes(stepId)) {
      setMsg("Compute Payroll is required after unlock before Review / Lock.");
      setActive("compute");
      return;
    }
    setActive(stepId);
  };

  const goToReview = async () => {
    if (needsCompute) {
      setMsg("Compute Payroll is required after unlock before Review.");
      setActive("compute");
      return;
    }
    setActive("review");
    const pr = await api.get("/payslips", { params: { month, year } });
    setPayslips(pr.data || []);
  };

  const totals = useMemo(() => payslips.reduce((acc, p) => {
    acc.gross += Number(p.gross_earnings || 0);
    acc.net   += Number(p.net_pay || 0);
    return acc;
  }, { gross: 0, net: 0 }), [payslips]);

  const isLocked = run?.status === "locked";

  return (
    <AdminLayout title="Payroll Run Wizard" breadcrumbs={["Home", "Payroll", "Run Wizard"]}>
      <div className="space-y-4">
        <FilterBar
          filters={[
            { type: "month", key: "month", label: "Pay Month", value: month, onChange: setMonth },
            { type: "year",  key: "year",  label: "Year",       value: year,  onChange: setYear },
          ]}
          rightSlot={
            !run ? (
              <button onClick={startRun} disabled={busy} className="inline-flex items-center gap-2 bg-[#226DB4] hover:bg-[#1B5A94] text-white px-4 py-2 text-sm rounded font-medium" data-testid="wizard-start-btn">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />}
                Start Payroll Run
              </button>
            ) : (
              <div className="flex items-center gap-2">
                <span className={`px-3 py-2 text-sm rounded font-medium ${isLocked ? "bg-rose-100 text-rose-700" : "bg-emerald-100 text-emerald-700"}`} data-testid="wizard-status-badge">
                  {isLocked ? "LOCKED" : "DRAFT"} — {MONTHS[month - 1]} {year}
                </span>
                {isLocked && isAdmin && (
                  <button
                    type="button"
                    onClick={() => { setActive("lock"); setUnlockOpen(true); setUnlockErr(""); setUnlockReason(""); }}
                    className="inline-flex items-center gap-1.5 px-3 py-2 text-sm rounded font-medium bg-slate-700 hover:bg-slate-800 text-white"
                    data-testid="wizard-unlock-badge-btn"
                  >
                    <Unlock className="h-4 w-4" /> Unlock
                  </button>
                )}
              </div>
            )
          }
        />

        {/* Pre-run error banner — surfaces failures from startRun (e.g. locked period) */}
        {!run && msg && (
          <div className="bg-rose-50 border border-rose-200 text-rose-800 text-sm rounded p-3" data-testid="wizard-pre-run-error">
            {msg}
          </div>
        )}

        {run && (
          <>
            {/* Stepper */}
            <div className="bg-white rounded-lg shadow border border-slate-200 p-4">
              <div className="flex items-center" data-testid="wizard-stepper">
                {STEPS.map((s, idx) => {
                  const done =
                    !needsCompute &&
                    ((run.steps_completed || []).includes(s.id) ||
                      (s.id === "review" && (run.steps_completed || []).includes("compute")));
                  const isActive = active === s.id;
                  const blocked = needsCompute && ["review", "lock", "disburse"].includes(s.id);
                  return (
                    <div key={s.id} className="flex items-center flex-1">
                      <button
                        onClick={() => goToStep(s.id)}
                        data-testid={`wizard-step-${s.id}`}
                        className={`flex items-center gap-2 px-3 py-2 rounded ${isActive ? "bg-[#226DB4] text-white" : blocked ? "text-slate-300 cursor-not-allowed" : done ? "text-emerald-600" : "text-slate-400"}`}
                        title={blocked ? "Compute required after unlock" : undefined}
                      >
                        <s.icon className="h-4 w-4" />
                        <span className="text-sm font-medium">{s.label}</span>
                        {done && !isActive && <CheckCircle2 className="h-4 w-4 text-emerald-500" />}
                      </button>
                      {idx < STEPS.length - 1 && <div className={`flex-1 h-px ${done ? "bg-emerald-300" : "bg-slate-200"} mx-2`} />}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Step body */}
            <div className="bg-white rounded-lg shadow border border-slate-200 p-5">
              {msg && <div className="mb-3 text-sm bg-blue-50 border border-blue-200 text-blue-800 rounded p-2" data-testid="wizard-msg">{msg}</div>}

              {active === "validate" && (
                <div data-testid="wizard-validate-panel">
                  <h3 className="font-semibold text-slate-800 mb-3">Step 1 — Validate Eligibility</h3>
                  <p className="text-sm text-slate-500 mb-3">
                    Verify salary and bank for everyone with Include in payment=Yes. Compute creates payroll rows for all of them;
                    Generate payslip=No means payment-only (no employee ESS/PDF slip).
                  </p>
                  <button onClick={runValidate} disabled={busy} className="bg-[#226DB4] hover:bg-[#1B5A94] text-white px-4 py-2 text-sm rounded inline-flex items-center gap-2" data-testid="wizard-validate-btn">
                    {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />} Run Validation
                  </button>
                  {validation && (
                    <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3">
                      <div className="border border-slate-200 rounded p-3"><div className="text-xs text-slate-500">In payment</div><div className="text-2xl font-semibold">{validation.total_payment ?? validation.total_eligible}</div></div>
                      <div className="border border-slate-200 rounded p-3"><div className="text-xs text-slate-500">Employee slips</div><div className="text-2xl font-semibold">{validation.total_eligible}</div></div>
                      <div className="border border-rose-200 bg-rose-50 rounded p-3"><div className="text-xs text-rose-600">Missing Salary</div><div className="text-2xl font-semibold text-rose-700">{validation.missing_salary?.length || 0}</div></div>
                      <div className="border border-amber-200 bg-amber-50 rounded p-3"><div className="text-xs text-amber-700">Missing Bank</div><div className="text-2xl font-semibold text-amber-800">{validation.missing_bank?.length || 0}</div></div>
                      {(validation.payment_only_count > 0) && (
                        <div className="col-span-2 md:col-span-4 text-sm bg-[#E8F0FF] border border-[#c7dbf5] rounded p-3">
                          <div className="font-medium text-slate-800 mb-1">
                            Payment only (no employee slip): {validation.payment_only_count}
                          </div>
                          <p className="text-xs text-slate-600 mb-1">
                            Will be included in Compute / salary register / bank. No ESS or employee PDF (typical Intern stipend).
                          </p>
                          {validation.payment_only?.length > 0 && (
                            <ul className="list-disc ml-5 text-slate-700">
                              {validation.payment_only.slice(0, 15).map((e) => (
                                <li key={e.id}>{e.code} — {e.name} ({e.employment_type || "—"})</li>
                              ))}
                            </ul>
                          )}
                        </div>
                      )}
                      {validation.missing_salary?.length > 0 && (
                        <div className="col-span-2 md:col-span-4 text-sm bg-rose-50 border border-rose-200 rounded p-3">
                          <div className="font-medium text-rose-700 mb-1">Missing Salary:</div>
                          <ul className="list-disc ml-5 text-rose-700">{validation.missing_salary.map((e) => <li key={e.id}>{e.code} — {e.name}</li>)}</ul>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {active === "compute" && (
                <div data-testid="wizard-compute-panel">
                  <h3 className="font-semibold text-slate-800 mb-3">Step 2 — Compute Payroll</h3>
                  {needsCompute && (
                    <div className="mb-3 text-sm bg-amber-50 border border-amber-200 text-amber-900 rounded p-3">
                      Month was unlocked. <b>Compute is mandatory</b> before Review / Lock so Include in payment
                      changes are applied.
                    </div>
                  )}
                  <p className="text-sm text-slate-500 mb-3">
                    Create payroll payment rows for Include in payment=Yes ({MONTHS[month - 1]} {year}).
                    Generate payslip=No → amounts on register/bank only (no slip View/PDF).
                  </p>
                  <button onClick={runCompute} disabled={busy || isLocked} className="bg-[#089949] hover:bg-[#067A3A] text-white px-4 py-2 text-sm rounded inline-flex items-center gap-2" data-testid="wizard-compute-btn">
                    {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />} Compute Payroll
                  </button>
                </div>
              )}

              {active === "review" && (
                <div data-testid="wizard-review-panel">
                  <h3 className="font-semibold text-slate-800 mb-3">Step 3 — Review</h3>
                  <button onClick={goToReview} className="mb-3 text-sm text-[#226DB4] underline" data-testid="wizard-review-refresh">Refresh data</button>
                  <div className="grid grid-cols-3 gap-3 mb-3">
                    <div className="border border-slate-200 rounded p-3"><div className="text-xs text-slate-500">Payroll rows</div><div className="text-2xl font-semibold">{payslips.length}</div></div>
                    <div className="border border-slate-200 rounded p-3"><div className="text-xs text-slate-500">Total Gross</div><div className="text-xl font-semibold">₹{totals.gross.toLocaleString("en-IN")}</div></div>
                    <div className="border border-slate-200 rounded p-3"><div className="text-xs text-slate-500">Total Net</div><div className="text-xl font-semibold text-emerald-700">₹{totals.net.toLocaleString("en-IN")}</div></div>
                  </div>
                  <p className="text-sm text-slate-500 mb-2">
                    Includes payment-only rows (no employee slip). Open Payroll → month <b>July</b> and look for
                    EMP110 with a <b>Payment only</b> badge. Unlock alone does not re-include anyone — you must
                    run Compute after changing flags.
                  </p>
                  <button
                    type="button"
                    onClick={() => setActive("compute")}
                    className="mb-3 text-sm text-[#226DB4] underline"
                  >
                    Back to Compute (re-reconcile flags)
                  </button>
                  <p className="text-sm text-slate-500">Once reviewed, lock the payroll to freeze the records.</p>
                  <button onClick={() => goToStep("lock")} className="mt-3 bg-slate-700 hover:bg-slate-800 text-white px-4 py-2 text-sm rounded inline-flex items-center gap-2" data-testid="wizard-goto-lock">
                    Continue to Lock →
                  </button>
                </div>
              )}

              {active === "lock" && (
                <div data-testid="wizard-lock-panel">
                  <h3 className="font-semibold text-slate-800 mb-3">Step 4 — Lock</h3>
                  <p className="text-sm text-slate-500 mb-3">
                    Lock freezes payslip edits for this month. Unlock (admin + reason) allows minor changes without voiding payslips.
                    Reverse Run voids the month so you can recompute from scratch.
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <button onClick={runLock} disabled={busy || isLocked} className="bg-rose-600 hover:bg-rose-700 disabled:bg-slate-300 text-white px-4 py-2 text-sm rounded inline-flex items-center gap-2" data-testid="wizard-lock-btn">
                      {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Lock className="h-4 w-4" />} Lock Payroll Run
                    </button>
                    {isLocked && isAdmin && (
                      <button
                        onClick={() => { setUnlockOpen(true); setUnlockErr(""); setUnlockReason(""); }}
                        disabled={busy}
                        className="bg-slate-700 hover:bg-slate-800 text-white px-4 py-2 text-sm rounded inline-flex items-center gap-2"
                        data-testid="wizard-unlock-btn"
                      >
                        <Unlock className="h-4 w-4" /> Unlock (admin)
                      </button>
                    )}
                  </div>
                  {isLocked && (
                    <div className="mt-3 text-sm bg-rose-50 border border-rose-200 rounded p-3">
                      Locked at {run.locked_at}. Totals: Gross ₹{Number(run.totals?.gross || 0).toLocaleString("en-IN")} · Net ₹{Number(run.totals?.net || 0).toLocaleString("en-IN")}
                    </div>
                  )}

                  {Array.isArray(run?.unlock_history) && run.unlock_history.length > 0 && (
                    <details className="mt-3 text-xs" data-testid="unlock-history">
                      <summary className="cursor-pointer text-slate-600 hover:text-slate-800">
                        Unlock history ({run.unlock_history.length})
                      </summary>
                      <ul className="mt-2 space-y-1.5">
                        {[...run.unlock_history].reverse().map((h) => (
                          <li key={h.id || h.unlocked_at} className="border-l-2 border-slate-200 pl-2 py-1">
                            <span className="text-slate-700">{h.reason}</span>
                            <div className="text-slate-500">
                              by {h.unlocked_by_name} on {h.unlocked_at ? new Date(h.unlocked_at).toLocaleString() : "—"}
                            </div>
                          </li>
                        ))}
                      </ul>
                    </details>
                  )}

                  {/* Phase 17 — Reverse Run (admin only, maker-checker) */}
                  {isLocked && isAdmin && (
                    <div className="mt-5 border-t border-slate-200 pt-4" data-testid="reverse-section">
                      <h4 className="text-sm font-semibold text-slate-700 mb-2 flex items-center gap-2">
                        <RotateCcw className="h-4 w-4 text-amber-600" /> Reverse Payroll Run
                      </h4>
                      {!reversal.pending && (
                        <>
                          <p className="text-xs text-slate-500 mb-2">
                            Distinct from Unlock: Reverse voids all payslips (maker-checker). Unlock keeps payslips and only re-opens edits.
                          </p>
                          <button
                            onClick={() => { setReverseOpen(true); setReverseErr(""); setReverseReason(""); }}
                            className="px-3 py-1.5 text-xs bg-amber-50 border border-amber-300 text-amber-800 rounded hover:bg-amber-100 inline-flex items-center gap-1.5"
                            data-testid="reverse-request-btn"
                          >
                            <RotateCcw className="h-3.5 w-3.5" /> Request Reverse Run
                          </button>
                        </>
                      )}
                      {reversal.pending && (
                        <div className="bg-amber-50 border border-amber-200 rounded p-3 text-sm" data-testid="reverse-pending-card">
                          <div className="flex items-center justify-between mb-1.5">
                            <div className="font-semibold text-amber-800">Pending Reversal Request</div>
                            <span className="text-[11px] px-2 py-0.5 bg-amber-200 text-amber-900 rounded">PENDING</span>
                          </div>
                          <div className="text-xs text-amber-800 mb-1">
                            <b>Requested by:</b> {reversal.pending.requested_by_name} ·{" "}
                            <b>At:</b> {new Date(reversal.pending.requested_at).toLocaleString()}
                          </div>
                          <div className="text-xs text-slate-700 mb-2">
                            <b>Reason:</b> {reversal.pending.reason}
                          </div>
                          {reversal.pending.requested_by === user?.id ? (
                            <div className="text-xs text-slate-500 italic">
                              Awaiting approval from another admin. You cannot approve your own reversal request.
                            </div>
                          ) : (
                            <div className="flex gap-2">
                              <button
                                onClick={() => { setDecideOpen("approve"); setDecideNote(""); setDecideErr(""); }}
                                className="px-3 py-1.5 text-xs bg-emerald-600 hover:bg-emerald-700 text-white rounded inline-flex items-center gap-1"
                                data-testid="reverse-approve-btn"
                              >
                                <CheckCircle2 className="h-3.5 w-3.5" /> Approve
                              </button>
                              <button
                                onClick={() => { setDecideOpen("reject"); setDecideNote(""); setDecideErr(""); }}
                                className="px-3 py-1.5 text-xs bg-slate-200 hover:bg-slate-300 text-slate-800 rounded inline-flex items-center gap-1"
                                data-testid="reverse-reject-btn"
                              >
                                Reject
                              </button>
                            </div>
                          )}
                        </div>
                      )}
                      {reversal.history.length > 0 && (
                        <details className="mt-3 text-xs">
                          <summary className="cursor-pointer text-slate-600 hover:text-slate-800">
                            Reversal history ({reversal.history.length})
                          </summary>
                          <ul className="mt-2 space-y-1.5" data-testid="reverse-history-list">
                            {reversal.history.map((h) => (
                              <li key={h.id} className="border-l-2 border-slate-200 pl-2 py-1">
                                <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${
                                  h.status === "approved" ? "bg-emerald-100 text-emerald-700"
                                  : h.status === "rejected" ? "bg-slate-200 text-slate-700"
                                  : "bg-amber-100 text-amber-700"
                                }`}>{h.status.toUpperCase()}</span>{" "}
                                <span className="text-slate-700">{h.reason}</span>
                                <div className="text-slate-500">
                                  by {h.requested_by_name} on {new Date(h.requested_at).toLocaleString()}
                                  {h.approved_by_name && <> · decided by {h.approved_by_name}</>}
                                </div>
                              </li>
                            ))}
                          </ul>
                        </details>
                      )}
                    </div>
                  )}
                </div>
              )}

              {active === "disburse" && (
                <div data-testid="wizard-disburse-panel">
                  <h3 className="font-semibold text-slate-800 mb-3">Step 5 — Disburse</h3>
                  <p className="text-sm text-slate-500 mb-3">
                    Download the salary register and mark payslips Paid after disbursement. Marking Paid is allowed even when the month is locked.
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={() => downloadFile(`/exports/salary-register.xlsx?month=${month}&year=${year}`, `salary_register_${year}_${String(month).padStart(2, "0")}.xlsx`)}
                      className="px-4 py-2 text-sm border border-slate-200 rounded hover:bg-slate-50 inline-flex items-center gap-2"
                      data-testid="wizard-download-register"
                    >
                      Download Salary Register (Excel)
                    </button>
                    <button
                      type="button"
                      onClick={() => downloadFile(`/exports/salary-register.pdf?month=${month}&year=${year}`, `salary_register_${year}_${String(month).padStart(2, "0")}.pdf`)}
                      className="px-4 py-2 text-sm border border-slate-200 rounded hover:bg-slate-50 inline-flex items-center gap-2"
                      data-testid="wizard-download-register-pdf"
                    >
                      Download Salary Register (PDF)
                    </button>
                    <button
                      type="button"
                      onClick={markAllPaid}
                      disabled={busy}
                      className="px-4 py-2 text-sm bg-emerald-600 hover:bg-emerald-700 text-white rounded inline-flex items-center gap-2 disabled:opacity-70"
                      data-testid="wizard-mark-paid-btn"
                    >
                      {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Banknote className="h-4 w-4" />}
                      Mark all payslips Paid
                    </button>
                  </div>
                  <div className="mt-4 text-xs text-slate-500">
                    <AlertCircle className="h-3.5 w-3.5 inline mr-1" />
                    Bank-advice CSV / NEFT file generation is coming in the next iteration.
                  </div>
                </div>
              )}
            </div>
          </>
        )}

        {/* Unlock modal (admin + reason; keeps payslips) */}
        {unlockOpen && (
          <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="unlock-modal">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6 space-y-4">
              <h3 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
                <Unlock className="h-5 w-5 text-slate-700" /> Unlock Payroll Month
              </h3>
              <p className="text-sm text-slate-600">
                Unlock <b>{MONTHS[month - 1]} {year}</b> to allow minor payslip edits. Payslips are kept.
                Use Reverse Run if you need to void and recompute the month.
              </p>
              <div>
                <label className="block text-xs font-semibold text-slate-600 uppercase mb-1">Reason *</label>
                <textarea
                  required
                  rows={4}
                  value={unlockReason}
                  onChange={(e) => setUnlockReason(e.target.value)}
                  placeholder="Why are you unlocking this month? (mandatory)"
                  className="w-full border border-slate-300 rounded px-3 py-2 text-sm"
                  data-testid="unlock-reason-input"
                />
              </div>
              {unlockErr && <div className="text-sm bg-rose-50 border border-rose-200 text-rose-800 rounded p-2">{unlockErr}</div>}
              <div className="flex justify-end gap-2 pt-1">
                <button onClick={() => setUnlockOpen(false)} className="px-4 py-2 text-sm border border-slate-200 rounded">Cancel</button>
                <button
                  onClick={submitUnlock}
                  disabled={busy}
                  className="px-4 py-2 text-sm bg-slate-700 hover:bg-slate-800 text-white rounded inline-flex items-center gap-1 disabled:opacity-70"
                  data-testid="unlock-submit-btn"
                >
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Unlock className="h-4 w-4" />}
                  Unlock
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Reverse Request modal */}
        {reverseOpen && (
          <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="reverse-request-modal">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6 space-y-4">
              <h3 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
                <RotateCcw className="h-5 w-5 text-amber-600" /> Request Reverse Run
              </h3>
              <p className="text-sm text-slate-600">
                You are requesting to reverse the locked payroll for <b>{MONTHS[month - 1]} {year}</b>.
                This will be sent to another admin for approval.
              </p>
              <div>
                <label className="block text-xs font-semibold text-slate-600 uppercase mb-1">Reason *</label>
                <textarea
                  required
                  rows={4}
                  value={reverseReason}
                  onChange={(e) => setReverseReason(e.target.value)}
                  placeholder="Why does this payroll need to be reversed? (mandatory)"
                  className="w-full border border-slate-300 rounded px-3 py-2 text-sm"
                  data-testid="reverse-reason-input"
                />
              </div>
              {reverseErr && <div className="text-sm bg-rose-50 border border-rose-200 text-rose-800 rounded p-2">{reverseErr}</div>}
              <div className="flex justify-end gap-2 pt-1">
                <button onClick={() => setReverseOpen(false)} className="px-4 py-2 text-sm border border-slate-200 rounded">Cancel</button>
                <button
                  onClick={submitReverseRequest}
                  disabled={busy}
                  className="px-4 py-2 text-sm bg-amber-600 hover:bg-amber-700 text-white rounded inline-flex items-center gap-1 disabled:opacity-70"
                  data-testid="reverse-submit-btn"
                >
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <RotateCcw className="h-4 w-4" />}
                  Submit Request
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Approve / Reject modal */}
        {decideOpen && (
          <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="reverse-decide-modal">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6 space-y-4">
              <h3 className="text-lg font-semibold text-slate-800">
                {decideOpen === "approve" ? "Approve Reversal Request" : "Reject Reversal Request"}
              </h3>
              <p className="text-sm text-slate-600">
                {decideOpen === "approve"
                  ? "Approving will soft-void all payslips for this period and reset the run to DRAFT. This action is logged in the audit trail."
                  : "Rejecting will keep the run LOCKED and close this request."}
              </p>
              <div>
                <label className="block text-xs font-semibold text-slate-600 uppercase mb-1">
                  Decision Note (optional)
                </label>
                <textarea
                  rows={3}
                  value={decideNote}
                  onChange={(e) => setDecideNote(e.target.value)}
                  placeholder="Optional note for audit trail"
                  className="w-full border border-slate-300 rounded px-3 py-2 text-sm"
                  data-testid="reverse-decision-note"
                />
              </div>
              {decideErr && <div className="text-sm bg-rose-50 border border-rose-200 text-rose-800 rounded p-2">{decideErr}</div>}
              <div className="flex justify-end gap-2 pt-1">
                <button onClick={() => setDecideOpen(null)} className="px-4 py-2 text-sm border border-slate-200 rounded">Cancel</button>
                <button
                  onClick={submitDecision}
                  disabled={busy}
                  className={`px-4 py-2 text-sm text-white rounded inline-flex items-center gap-1 disabled:opacity-70 ${
                    decideOpen === "approve" ? "bg-emerald-600 hover:bg-emerald-700" : "bg-slate-700 hover:bg-slate-800"
                  }`}
                  data-testid="reverse-decision-submit"
                >
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                  Confirm {decideOpen === "approve" ? "Approve" : "Reject"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
